import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService, ModalService, NotificationService, PatientService, SocketService } from '../../services';
import { environment } from '../../../../environments/environment';
import { catchError, distinctUntilChanged, map, shareReplay, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';
import { NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Notification } from 'rxjs/Notification';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TimerObservable } from 'rxjs/observable/TimerObservable';
import { VoipWorkstationService } from '../../../voip/services/voip-workstation.service';

const io = require('socket.io-client/dist/socket.io.js');
const webNotification = require('simple-web-notification/web-notification.js');

interface Alert {
    type: string;
    message: string;
    timestamp: Date;
}

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit, OnDestroy {

    private one$: Subscription;
    private two$: Subscription;
    private twoEBS$: Subscription;
    private three$: Subscription;
    private four$: Subscription;
    private five$: Subscription;
    private six$: Subscription;
    private routeSubscription: Subscription;
    private flag = true;
    private telecentrumMode = false;
    private sessionCheck$: Observable<boolean>;
    private sessionCheckedAt = 0;
    public alerts: Alert[] = [];

    constructor(
        private userService: UserService,
        private modalService: ModalService,
        private notificationService: NotificationService,
        private patientService: PatientService,
        private socketService: SocketService,
        private ngZone: NgZone,
        private translate: TranslateService,
        private router: Router,
        private workstationService: VoipWorkstationService
    ) { }

    /**
     * Telecentrum zastępuje okno SOS/upadku przekierowaniem z połączenia — ale to działa tylko
     * przy sesji na stanowisku. Bez niej rozmowę odbiera się na aparacie i nic nie wyskakuje
     * (5.10.2026, 8:10 i 8:18 — luka między zmianami). Wtedy, i gdy sesji nie da się sprawdzić,
     * okno wraca: lepiej pokazać je raz za dużo niż przegapić alarm.
     */
    private openSosAlertIfNeeded(user) {
        if (!this.telecentrumMode) {
            this.modalService.openSosAlert(user);
            return;
        }
        this.hasWorkstationSession().subscribe(hasSession => {
            if (!hasSession) {
                this.ngZone.run(() => this.modalService.openSosAlert(user));
            }
        });
    }

    /**
     * Jedno sprawdzenie sesji na serię alarmów: restorePendingAlarms() i seria `sosAlert`
     * pytały `/my-session` osobno o każdy alarm. Wynik żyje 5 s; błąd = brak sesji.
     */
    private hasWorkstationSession(): Observable<boolean> {
        if (!this.sessionCheck$ || Date.now() - this.sessionCheckedAt > 5000) {
            this.sessionCheckedAt = Date.now();
            this.sessionCheck$ = this.workstationService.getMySession().pipe(
                map(session => !!(session && session.hasSession)),
                catchError(() => of(false)),
                shareReplay(1)
            );
        }
        return this.sessionCheck$;
    }

    ngOnInit() {

        this.one$ = this.userService.isAuthenticated
            .pipe(distinctUntilChanged())
            .subscribe(data => {
                if (data) {

                    this.ngZone.runOutsideAngular(() => {

                        TimerObservable.create(0, 30000).subscribe(() => {
                            this.clearOldAlerts();
                        });

                        this.socketService.init(this.userService.getCurrentUser().user_id.id);

                        // Tryb telecentrum: alarm z ramki opaski (SOS / PRE / FALL) nie otwiera okna, gdy
                        // ratownik ma sesję na stanowisku — do obsługi prowadzi połączenie (widget VoIP),
                        // jak na TC. Bez sesji okno wraca (openSosAlertIfNeeded). Pozostałe konta bez zmian.
                        this.userService.isTelecentrumMode().pipe(take(1)).subscribe(mode => {
                            this.telecentrumMode = mode;
                            // Przywróć alarmy z sessionStorage po odświeżeniu strony
                            this.restorePendingAlarms();
                        });

                        this.two$ = this.socketService.onAlert()
                            .subscribe(user => {
                                this.ngZone.run(() => {
                                    console.log('📢 [NOTIFICATIONS] Received alarm in notifications component:', user);
                                    this.notificationService.addUserAlarm(user);
                                    this.openSosAlertIfNeeded(user);
                                    this.createNotification(user);
                                });
                            });

                        this.twoEBS$ = this.socketService.onAlertEBS()
                            .subscribe(user => {
                                this.ngZone.run(() => {
                                    if (this.flag) {
                                        this.notificationService.addUserEBSAlarm(user);
                                        this.modalService.openSensorAlert(user);
                                        this.createNotification(user);
                                    }
                                });
                            });

                        this.three$ = this.socketService.onAccept()
                            .subscribe(user => {
                                this.ngZone.run(() => {
                                    this.notificationService.deleteUserAlarm(user);
                                });
                            });

                        this.four$ = this.socketService.onNotification()
                            .subscribe(user => {
                                this.ngZone.run(() => {
                                    this.createNotificationStandard(user);
                                });
                            });
                        this.five$ = this.socketService.snackbarAH().subscribe(datum => {
                            this.alerts.push({
                                type: 'warning',
                                message: this.translate.instant('CAREGIVER.USER-CONFIRMED-ALARM', { name: datum.name }),
                                timestamp: new Date()
                            });
                        });

                        this.six$ = this.socketService.snackbar().subscribe(datum => {
                            if (this.router.url.includes('users/sos-request')) {
                                this.alerts.push({
                                    type: 'danger',
                                    message: this.translate.instant('CONNECTING-SOON.MESSAGE', { name: datum.name, typeOfAlarm: this.translate.instant(`CONNECTING-SOON.${datum.typeOfAlarm}`) }),
                                    timestamp: new Date()
                                });
                            }
                        });
                    });

                } else {
                    this.logoutNotifications();
                }
            });

    }

    private restorePendingAlarms() {
        // Nie przywracaj alarmów jeśli użytkownik jest już na stronie obsługi alarmu
        if (this.router.url.includes('users/sos-request') || this.router.url.includes('users/ebs-request')) {
            return;
        }

        const pendingAlarms = this.notificationService.getPendingAlarms();
        if (pendingAlarms.length > 0) {
            this.ngZone.run(() => {
                pendingAlarms.forEach(user => {
                    if (user.evt) {
                        this.notificationService.addUserEBSAlarm(user);
                        this.modalService.openSensorAlert(user);
                    } else {
                        this.notificationService.addUserAlarm(user);
                        this.openSosAlertIfNeeded(user);
                    }
                });
            });
        }
    }

    createNotification(user) {
        webNotification.showNotification('SIDLY HEALTH ALERT', {
            body: user.last_name + ' ' + user.first_name,
            icon: 'assets/images/97ba719063ec81a42d3b96d17f0bbadec79528ebd34430170e898be9045a2fd7.png',
            onClick: function onNotificationClicked() {
            },
            autoClose: 600000
        }, function onShow(error, hide) {
            /*
            if (error) {
                //window.alert('Unable to show notification: ' + error.message);
            }
            */
        });
    }

    clearOldAlerts() {
        const now = new Date();
        this.alerts = this.alerts.filter(alert => {
            const timeDifference = now.getTime() - alert.timestamp.getTime();
            return timeDifference <= 30000;
        });
    }

    //Prog tetna
    createNotificationStandard(user) {
        webNotification.showNotification('SIDLY HEALTH ALERT', {

            body: user.userDataPack.last_name + ' ' + user.userDataPack.first_name + ': ' + this.translate.instant('BACKEND.' + user.message),
            icon: 'assets/images/97ba719063ec81a42d3b96d17f0bbadec79528ebd34430170e898be9045a2fd7.png',
            onClick: function onNotificationClicked() {
            },
            vibrate: [200, 100, 200, 100, 200, 100, 200],
            autoClose: 600000
        }, function onShow(error, hide) {
            /*
            if (error) {
                //window.alert('Unable to show notification: ' + error.message);
            }
            */
        });
    }

    logoutNotifications() {
        if (this.two$) {
            this.two$.unsubscribe();
        }
        if (this.twoEBS$) {
            this.twoEBS$.unsubscribe();
        }
        if (this.three$) {
            this.three$.unsubscribe();
        }
        if (this.four$) {
            this.four$.unsubscribe();
        }
        if (this.five$) {
            this.five$.unsubscribe();
        }
        if (this.six$) {
            this.six$.unsubscribe();
        }
        this.notificationService.clean();

        this.socketService.close();
    }

    close(alert: Alert) {
        this.alerts.splice(this.alerts.indexOf(alert), 1);
    }

    ngOnDestroy() {
        if (this.one$) {
            this.one$.unsubscribe();
        }
        this.logoutNotifications();
    }
}
